// ============================================================================
// Attribution, batched. 2026-09-02.
//
// TWO queries for a whole board, never one per card. `fetchRateBasisMap` in
// account-management/my-accounts.tsx is the pattern: an `.in()` over the ids
// already on screen, folded into a Map, no PostgREST embed.
//
// ⛔ PICKERS EXCLUDE, RESOLVERS RESOLVE, AND THEY ARE DIFFERENT VIEWS
// (TCSD-0129 / TCSD-0163). This is a RESOLVER — it renders names for people who
// may have left. It reads `v_employee_display`, which returns every employment
// status on purpose. Two Former employees are project creators and their names
// must still appear. ⛔ Never repoint this at `v_employee_picker`: that view is
// Active + Salaried + app_role and was narrowed further on 2026-09-02, so it
// would silently blank them. The mechanical test (PLAYBOOK) is that a call site
// using `.in('employee_id', …)` is a resolver — this one does.
// ============================================================================
import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type { AttributionRow, NameLookup } from '../lib/attribution';

export interface AttributionMap {
  rows: Map<string, AttributionRow>;
  nameFor: NameLookup;
  isLoading: boolean;
}

const EMPTY: AttributionMap = {
  rows: new Map(),
  nameFor: () => null,
  isLoading: false,
};

/** One `.in()` over v_project_attribution, then one over the names it needs. */
export async function fetchProjectAttribution(
  projectIds: string[],
): Promise<{ rows: Map<string, AttributionRow>; names: Map<string, string> }> {
  const rows = new Map<string, AttributionRow>();
  const names = new Map<string, string>();
  if (projectIds.length === 0) return { rows, names };

  const { data, error } = await supabase
    .from('v_project_attribution')
    .select('project_id, entered_by_eid, entered_by_source, owner_eid, owner_basis')
    .in('project_id', projectIds);
  // Attribution is a byline. If it fails the card still renders — it just says
  // nothing rather than guessing.
  if (error) return { rows, names };

  for (const r of (data ?? []) as AttributionRow[]) {
    if (r.project_id) rows.set(r.project_id, r);
  }

  const eids = Array.from(
    new Set(
      Array.from(rows.values())
        .flatMap((r) => [r.entered_by_eid, r.owner_eid])
        .filter((e): e is string => !!e),
    ),
  );
  if (eids.length === 0) return { rows, names };

  const { data: people, error: peopleError } = await supabase
    .from('v_employee_display')
    .select('employee_id, employee_full_name')
    .in('employee_id', eids);
  if (peopleError) return { rows, names };

  for (const p of (people ?? []) as Array<{
    employee_id: string | null;
    employee_full_name: string | null;
  }>) {
    if (p.employee_id && p.employee_full_name) names.set(p.employee_id, p.employee_full_name);
  }
  return { rows, names };
}

/**
 * Attribution for a page of projects. Pass the ids already on screen; the hook
 * refetches only when that list actually changes, so scrolling a board does not
 * re-query.
 */
export function useProjectAttribution(projectIds: string[]): AttributionMap {
  const [state, setState] = useState<AttributionMap>(EMPTY);
  const key = projectIds.join(',');

  useEffect(() => {
    let cancelled = false;
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setState(EMPTY);
      return;
    }
    setState((s) => ({ ...s, isLoading: true }));
    void fetchProjectAttribution(ids).then(({ rows, names }) => {
      if (cancelled) return;
      setState({
        rows,
        nameFor: (eid) => (eid ? (names.get(eid) ?? null) : null),
        isLoading: false,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}
